// Subscriber ad removal. When the publisher confirms a visitor is entitled to
// ad-free browsing (their own hasAccess check), they call disableAds(); the
// widget mints a short-lived signed token from the backend (issued only to a
// visitor with a real entitlement) and stores it in a reserved first-party
// cookie that Sol verifies server-side (staticsol forwards document.cookie in
// the /sa.go body) to suppress every ad format. The publisher never touches the
// cookie — the widget owns its whole lifecycle, including the one-time reload
// needed to apply server-side suppression on the page where entitlement is first
// gained, and the periodic re-mint that keeps the token fresh.

import { mintAdRemovalToken, type AdRemovalMintResult } from '@/api/client';
import { AD_REMOVAL_COOKIE_NAME, adRemovalScope, clearAdRemovalCookie } from '@/session';
import { decodeJwtPayload } from '@/tokenPayload';
// Requested lifetime (~180d). Safari/WebKit clamps JS-set cookies to ~7 days
// regardless; disableAds rewrites the cookie on every already-suppressed load to
// keep active Safari visitors from hitting that eviction cliff. The token's own
// exp (the minting session's expiry) is the real backstop, not this max-age.
const COOKIE_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
// Re-mint a still-valid token once it is within this window of expiring, so an
// active visitor never reaches exp. Well under the session's 30d TTL.
const REFRESH_BEFORE_EXP_SECONDS = 3 * 24 * 60 * 60;
// reconcileAdRemoval revalidates an active cookie against the live entitlement at
// most this often, so a suppressed visitor is re-checked without a backend call
// on every pageview. It bounds how long a lapsed subscriber keeps ad-free after
// the lapse: at most one interval of active browsing. Matches the backend's
// pending-renewal grace so the two windows line up.
const RECONCILE_INTERVAL_SECONDS = 6 * 60 * 60;
// localStorage (not sessionStorage) so the throttle spans tabs and navigations.
const RECONCILE_AT_KEY = 'ezsm_remove_ads_reconciled_at';

// One-shot guard against reload loops: set before a reload, cleared on the next
// load once the cookie is confirmed present (see initAdRemoval). If the cookie
// fails to persist across the reload, the guard stays and blocks a second reload.
const RELOAD_GUARD_KEY = 'ezsm_remove_ads_reloaded';
// Scroll offset stashed before an immediate reload (login-mid-read), restored on
// the next load so the visitor keeps their place.
const SCROLL_KEY = 'ezsm_remove_ads_scroll';

// Open widget overlays (paywall/checkout/purchase). While any is open, disableAds
// defers its reload so it never tears down an on-screen success confirmation; the
// reload fires when the last overlay closes.
let openOverlays = 0;
// A reload owed to an ad-removal transition that happened while an overlay was
// open. Consumed by markOverlayClosed. In-memory only: the defer-then-reload
// happens within a single page life, so it needs no cross-reload persistence.
let reloadPending = false;
let inlineHolds = 0;

function readCookie(): string {
  const prefix = `${AD_REMOVAL_COOKIE_NAME}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return part.slice(prefix.length);
    }
  }
  return '';
}

function hasRemoveAdsCookie(): boolean {
  return readCookie() !== '';
}

function writeRemoveAdsCookie(token: string): void {
  document.cookie = `${AD_REMOVAL_COOKIE_NAME}=${token}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; secure; samesite=lax`;
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

// tokenExpSeconds reads the unverified exp claim from a JWT payload. This is only
// to schedule re-minting — the token is never trusted client-side (Sol verifies
// the signature). Returns null for anything unparseable.
function tokenExpSeconds(token: string): number | null {
  const exp = decodeJwtPayload(token)?.exp;
  return typeof exp === 'number' ? exp : null;
}

// A token that will not parse is treated as both near-expiry and expired, so the
// refresh path re-mints it and, failing that, relinquishes it.
function tokenNearExpiry(token: string): boolean {
  const exp = tokenExpSeconds(token);
  if (exp === null) {
    return true;
  }
  return exp - nowSeconds() < REFRESH_BEFORE_EXP_SECONDS;
}

function tokenExpired(token: string): boolean {
  const exp = tokenExpSeconds(token);
  if (exp === null) {
    return true;
  }
  return exp <= nowSeconds();
}

function safeGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode / disabled); the guard/scroll features
    // simply degrade — worst case is one extra reload or a lost scroll offset.
  }
}

function safeRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

// The reconcile throttle timestamp lives in localStorage so it persists across
// tabs and navigations (unlike the reload guard/scroll, which are per-load). A
// storage outage (private mode / disabled) simply makes every load "due", which
// is safe — worst case is an extra mint per load.
function safeLocalGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

// reconcileDue reports whether the ad-free cookie is due for a live-entitlement
// recheck. A missing or unparseable timestamp counts as due.
function reconcileDue(): boolean {
  const raw = safeLocalGet(RECONCILE_AT_KEY);
  if (raw === null) {
    return true;
  }
  const at = Number(raw);
  if (!Number.isFinite(at)) {
    return true;
  }
  return nowSeconds() - at >= RECONCILE_INTERVAL_SECONDS;
}

function markReconciled(): void {
  safeLocalSet(RECONCILE_AT_KEY, String(nowSeconds()));
}

// guardedReload reloads at most once per transition. The guard survives the
// reload and is cleared on the next load only when the cookie is present, so a
// cookie that refuses to persist can never drive an infinite reload loop.
function guardedReload(): void {
  if (safeGet(RELOAD_GUARD_KEY) === '1') {
    return;
  }
  safeSet(RELOAD_GUARD_KEY, '1');
  window.location.reload();
}

// applyMintResult reconciles a mint outcome against the current cookie:
//   - minted: adopt the fresh token.
//   - denied (403): the visitor is no longer entitled — revoke now, regardless of
//     the current token's exp.
//   - unavailable: cannot verify — fail open, keeping the current token while it
//     is still valid and only relinquishing once it has truly expired.
// Returns true when the outcome was conclusive (minted or denied), so a caller
// can decide whether to record it; an unavailable result is inconclusive.
function applyMintResult(result: AdRemovalMintResult, existing: string): boolean {
  if (result.status === 'minted') {
    writeRemoveAdsCookie(result.token);
    return true;
  }
  if (result.status === 'denied') {
    allowAds();
    return true;
  }
  // unavailable (401 / infra / network): keep while valid, relinquish once expired.
  if (tokenExpired(existing)) {
    allowAds();
    return false;
  }
  writeRemoveAdsCookie(existing);
  return false;
}

// mintIfCurrent mints and returns the result, or null when allowAds, a
// sign-out or a new sign-in happened while the mint was in flight.
async function mintIfCurrent(): Promise<AdRemovalMintResult | null> {
  const current = adRemovalScope.capture();
  const result = await mintAdRemovalToken();
  if (!current()) {
    return null;
  }
  return result;
}

// refreshSuppression runs when the cookie is already present (suppression is
// already active server-side, so it never reloads). It keeps the token fresh:
// rewrite the cookie to extend its max-age past Safari's ITP cap, and re-mint
// before the token expires. A denied re-mint revokes; a failed one fails open —
// keep the current token while it is still valid, relinquishing only once it has
// actually expired. Periodic revocation of a still-valid token is the reconcile
// path's job (this only mints near exp, to stay cheap on frequent calls).
async function refreshSuppression(existing: string): Promise<void> {
  if (!tokenNearExpiry(existing)) {
    writeRemoveAdsCookie(existing);
    return;
  }
  const result = await mintIfCurrent();
  if (!result) {
    return;
  }
  applyMintResult(result, existing);
}

// reconcileAdRemoval revalidates an existing ad-free cookie against the live
// entitlement and deletes it when the backend denies (403), so a lapsed
// subscriber's suppression ends automatically — independent of the publisher's
// access:change wiring and of the token's exp. It runs on load, throttled
// to RECONCILE_INTERVAL_SECONDS, and only ever *revalidates* an existing cookie:
// with no cookie there is nothing to revoke, and turning ad-free on stays
// publisher-driven (disableAds). A transient failure keeps suppression and does
// not advance the throttle, so it retries on the next load.
export async function reconcileAdRemoval(): Promise<void> {
  const existing = readCookie();
  if (!existing) {
    return;
  }
  if (!reconcileDue()) {
    // Not due, but still bump the cookie's max-age past Safari's ITP cap.
    writeRemoveAdsCookie(existing);
    return;
  }
  const result = await mintIfCurrent();
  if (!result) {
    return;
  }
  if (applyMintResult(result, existing)) {
    markReconciled();
  }
}

// disableAds suppresses ads for an entitled visitor. When no cookie is present it
// mints a signed token and, on the page where it is first set, reloads once so
// Sol can omit ads server-side (the only reliable way to remove already-rendered
// video/Humix). A denied/failed mint fails open (ads stay). When the cookie is
// already present it only refreshes the token (no reload).
export async function disableAds(): Promise<void> {
  const existing = readCookie();
  if (existing) {
    await refreshSuppression(existing);
    return;
  }

  const startedInline = inlineHolds > 0;
  const result = await mintIfCurrent();
  // Denied (no entitlement), unauthenticated, mint unavailable, or overtaken:
  // degrade to ad-supported and do not reload.
  if (!result || result.status !== 'minted') {
    return;
  }

  writeRemoveAdsCookie(result.token);
  // Blocked (cookies disabled / private / ITP): degrade to ad-supported and do
  // not reload, so we never loop trying to set a cookie that will not stick.
  if (!hasRemoveAdsCookie()) {
    return;
  }

  if (startedInline || inlineHolds > 0) {
    return;
  }

  // A success/paywall surface is on screen: defer the reload until it closes so
  // the visitor's confirmation is not destroyed mid-view.
  if (openOverlays > 0) {
    reloadPending = true;
    return;
  }

  // No overlay (a returning subscriber who logged in mid-read): reload now, but
  // preserve their scroll position across it.
  safeSet(SCROLL_KEY, String(window.scrollY));
  guardedReload();
}

// allowAds removes the suppression cookie when access is lost (logout, expired
// entitlement). It never reloads — ads simply return on the next navigation, so a
// visitor who just lost access is not hit with a jarring refresh.
export function allowAds(): void {
  reloadPending = false;
  clearAdRemovalCookie();
}

export function acquireInlineAdHold(): () => void {
  inlineHolds += 1;
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    inlineHolds -= 1;
  };
}

// Overlay closure releases deferred reloads; inline teardown does not.
export function markOverlayOpen(): void {
  openOverlays += 1;
}

export function markOverlayClosed(): void {
  if (openOverlays > 0) {
    openOverlays -= 1;
  }
  if (openOverlays > 0 || !reloadPending) {
    return;
  }
  // The visitor dismissed the surface after gaining ad-free access: apply it now.
  // No scroll stash — they were in a modal, not reading the page.
  reloadPending = false;
  if (hasRemoveAdsCookie()) {
    guardedReload();
  }
}

// initAdRemoval runs once on load: clear the reload guard when the cookie is
// present (the prior reload succeeded, so a future transition may reload again),
// and restore any scroll offset stashed before an immediate reload.
export function initAdRemoval(): void {
  if (hasRemoveAdsCookie()) {
    safeRemove(RELOAD_GUARD_KEY);
  }

  const stashed = safeGet(SCROLL_KEY);
  if (stashed === null) {
    return;
  }
  safeRemove(SCROLL_KEY);
  const y = Number(stashed);
  if (Number.isFinite(y)) {
    window.scrollTo(0, y);
  }
}
